import React, { useCallback, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface FFMatch {
  full_name: string;
  match_date?: string;
  relationship_range?: string;
  shared_cm: number;
  longest_cm: number;
  linked_relationship?: string;
  ancestral_surnames: string[];
  y_haplogroup?: string;
  mtdna_haplogroup?: string;
  notes?: string;
  bucket?: 'Paternal' | 'Maternal' | null;
  x_cm?: number | null;
}

interface Cluster {
  surname: string;
  matches: number;
  lift: number;
  in_tree_side: boolean;
  in_tree_other_side: boolean;
}

interface ComparisonRow {
  match_name: string;
  painter_sides: string[];
  painter_cm: number;
  ff_bucket?: string | null;
  ff_cm: number;
  status: 'conflict' | 'ff_adds_side' | 'agree' | 'ff_unknown';
}

interface Summary {
  total: number;
  by_bucket?: Record<string, number>;
  x_matches?: FFMatch[];
  strongest?: Record<string, FFMatch[]>;
  painter_comparison?: { counts: Record<string, number>; rows: ComparisonRow[] };
  surname_clusters?: Record<string, Cluster[]>;
}

const SIDES = ['Paternal', 'Maternal'] as const;
const PAGE = 100;

const bucketLabel = (b?: string | null) => (b === 'Paternal' ? "Father's side" : b === 'Maternal' ? "Mother's side" : 'Unknown');

const FamilyFinderPage: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [bucket, setBucket] = useState('');
  const [minCm, setMinCm] = useState('20');
  const [xOnly, setXOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [matches, setMatches] = useState<FFMatch[]>([]);
  const [matchTotal, setMatchTotal] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/family_finder/summary`);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      setSummary(await res.json());
      setError(null);
    } catch (e) {
      setError(`Could not load Family Finder data (${e instanceof Error ? e.message : 'unknown error'})`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMatches = useCallback(async (offset = 0) => {
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset), min_cm: minCm || '0' });
    if (bucket) params.set('bucket', bucket);
    if (xOnly) params.set('x_only', 'true');
    if (search.trim()) params.set('q', search.trim());
    try {
      const res = await fetch(`${API_URL}/family_finder/matches?${params}`);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      setMatchTotal(data.total);
      setMatches(prev => (offset === 0 ? data.matches : [...prev, ...data.matches]));
    } catch (e) {
      setError(`Could not load matches (${e instanceof Error ? e.message : 'unknown error'})`);
    }
  }, [bucket, minCm, xOnly, search]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    const t = setTimeout(() => loadMatches(0), 250);
    return () => clearTimeout(t);
  }, [loadMatches]);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setImporting(true);
    setImportMessage(null);
    try {
      const csvText = await file.text();
      const res = await fetch(`${API_URL}/family_finder/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv_text: csvText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `API returned ${res.status}`);
      const b = data.by_bucket || {};
      setImportMessage(`Imported ${data.imported} matches: ${b.Paternal || 0} father's side, ${b.Maternal || 0} mother's side, ${b.Unknown || 0} unknown.`);
      await loadSummary();
      await loadMatches(0);
    } catch (e) {
      setImportMessage(`Import failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const hasData = summary && summary.total > 0;
  const counts = summary?.painter_comparison?.counts || {};

  return (
    <section className="genealogy-section family-finder-page">
      <h1 className="heading-1">Family Finder</h1>
      <p className="family-finder-intro">
        Import a FamilyTreeDNA Family Finder match list (Matches → Export CSV). It has no segment positions, but it
        tells you which parent's side many matches are on, which matches share X-DNA, and matches' ancestral surnames.
        Importing again replaces the previous list.
      </p>

      <div className="genealogy-card family-finder-upload">
        <label className="genealogy-btn-primary family-finder-file-btn">
          {importing ? 'Importing…' : 'Choose Family Finder CSV'}
          <input type="file" accept=".csv,text/csv" disabled={importing} onChange={e => handleFile(e.target.files?.[0])} />
        </label>
        {importMessage && <div className="family-finder-import-message">{importMessage}</div>}
      </div>

      {error && <div className="family-finder-error">{error}</div>}
      {loading && !summary && !error && (
        <div className="family-finder-loading">Loading Family Finder data… the first load after an import can take a few seconds.</div>
      )}

      {hasData && (
        <>
          <div className="family-finder-stats">
            <div className="genealogy-card"><strong>{summary!.total.toLocaleString()}</strong><span>matches</span></div>
            {SIDES.map(side => (
              <div className="genealogy-card" key={side}>
                <strong>{(summary!.by_bucket?.[side] || 0).toLocaleString()}</strong><span>{bucketLabel(side)}</span>
              </div>
            ))}
            <div className="genealogy-card"><strong>{(summary!.by_bucket?.Unknown || 0).toLocaleString()}</strong><span>side unknown</span></div>
            <div className="genealogy-card"><strong>{summary!.x_matches?.length || 0}</strong><span>X-DNA matches</span></div>
          </div>

          <div className="genealogy-card family-finder-panel">
            <h2 className="heading-3">Sides compared with DNA Painter</h2>
            <p>
              {counts.agree || 0} agree · <span className="ff-conflict">{counts.conflict || 0} conflict</span> ·{' '}
              {counts.ff_adds_side || 0} where Family Finder knows a side DNA Painter doesn't · {counts.ff_unknown || 0} Family Finder can't place
            </p>
            {(summary!.painter_comparison?.rows.length || 0) > 0 && (
              <table className="family-finder-table">
                <thead><tr><th>Match</th><th>DNA Painter side</th><th>Painter cM</th><th>Family Finder side</th><th>FF cM</th><th /></tr></thead>
                <tbody>
                  {summary!.painter_comparison!.rows.map(r => (
                    <tr key={r.match_name} className={r.status === 'conflict' ? 'ff-row-conflict' : ''}>
                      <td>{r.match_name}</td>
                      <td>{r.painter_sides.join(', ') || '—'}</td>
                      <td>{r.painter_cm}</td>
                      <td>{bucketLabel(r.ff_bucket)}</td>
                      <td>{r.ff_cm}</td>
                      <td>{r.status === 'conflict' ? 'Check this match' : 'Side available'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="family-finder-columns">
            {SIDES.map(side => (
              <div className="genealogy-card family-finder-panel" key={side}>
                <h2 className="heading-3">Surname clusters: {bucketLabel(side).toLowerCase()}</h2>
                <p className="family-finder-hint">
                  Surnames that appear more often among this side's matches than among all matches.
                  <span className="ff-badge ff-badge-tree">in tree</span> means the surname is already among this side's ancestors.
                </p>
                {(summary!.surname_clusters?.[side] || []).length === 0 ? (
                  <p>No surname stands out on this side.</p>
                ) : (
                  <table className="family-finder-table">
                    <thead><tr><th>Surname</th><th>Matches</th><th>How much more often</th><th /></tr></thead>
                    <tbody>
                      {summary!.surname_clusters![side].map(c => (
                        <tr key={c.surname}>
                          <td><button type="button" className="ff-link" onClick={() => { setSearch(c.surname); setBucket(side); setMinCm('0'); }}>{c.surname}</button></td>
                          <td>{c.matches}</td>
                          <td>{c.lift}×</td>
                          <td>
                            {c.in_tree_side && <span className="ff-badge ff-badge-tree">in tree</span>}
                            {!c.in_tree_side && c.in_tree_other_side && <span className="ff-badge">other side of tree</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>

          {(summary!.x_matches?.length || 0) > 0 && (
            <div className="genealogy-card family-finder-panel">
              <h2 className="heading-3">X-DNA matches</h2>
              <p className="family-finder-hint">X-DNA is only inherited through certain ancestors, which narrows where these matches connect.</p>
              <table className="family-finder-table">
                <thead><tr><th>Match</th><th>Total cM</th><th>X cM</th><th>Side</th><th>Ancestral surnames</th></tr></thead>
                <tbody>
                  {summary!.x_matches!.map(m => (
                    <tr key={m.full_name}>
                      <td>{m.full_name}{m.linked_relationship ? ` (${m.linked_relationship})` : ''}</td>
                      <td>{m.shared_cm}</td>
                      <td>{m.x_cm}</td>
                      <td>{bucketLabel(m.bucket)}</td>
                      <td>{m.ancestral_surnames.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="genealogy-card family-finder-panel">
            <h2 className="heading-3">All matches</h2>
            <div className="family-finder-filters">
              <select value={bucket} onChange={e => setBucket(e.target.value)} aria-label="Side">
                <option value="">Any side</option>
                <option value="Paternal">Father's side</option>
                <option value="Maternal">Mother's side</option>
                <option value="Unknown">Unknown</option>
              </select>
              <label>Min cM <input type="number" min={0} value={minCm} onChange={e => setMinCm(e.target.value)} /></label>
              <label><input type="checkbox" checked={xOnly} onChange={e => setXOnly(e.target.checked)} /> X-DNA only</label>
              <input type="search" placeholder="Name or ancestral surname" value={search} onChange={e => setSearch(e.target.value)} />
              <span className="family-finder-hint">{matchTotal.toLocaleString()} matches</span>
            </div>
            <div className="family-finder-table-wrap">
              <table className="family-finder-table">
                <thead>
                  <tr><th>Match</th><th>Shared cM</th><th>Longest</th><th>Estimated relationship</th><th>Side</th><th>X</th><th>Ancestral surnames</th><th>Y / mt</th></tr>
                </thead>
                <tbody>
                  {matches.map(m => (
                    <tr key={`${m.full_name}-${m.shared_cm}`}>
                      <td>{m.full_name}{m.notes ? <div className="family-finder-hint">{m.notes}</div> : null}</td>
                      <td>{m.shared_cm}</td>
                      <td>{m.longest_cm}</td>
                      <td>{m.linked_relationship || m.relationship_range}</td>
                      <td>{bucketLabel(m.bucket)}</td>
                      <td>{m.x_cm ? `${m.x_cm} cM` : ''}</td>
                      <td className="ff-surnames">{m.ancestral_surnames.join(', ')}</td>
                      <td>{[m.y_haplogroup, m.mtdna_haplogroup].filter(Boolean).join(' / ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {matches.length < matchTotal && (
              <button type="button" className="genealogy-btn-secondary" onClick={() => loadMatches(matches.length)}>Show more</button>
            )}
          </div>
        </>
      )}
      {summary && summary.total === 0 && !error && <p>No Family Finder matches imported yet.</p>}
    </section>
  );
};

export default FamilyFinderPage;
